import { laneAssignmentLabel } from "../laneAssignment";
import { formatRecapLane } from "./importRecapScores";

export const AVERAGES_BLOCK_VERIFY_REASON =
  "Team cannot be verified because some bowlers' averages are not verified";

export const SCORES_WRITTEN_WAITING_LINE =
  "Scores were written. Import finished. Verify is waiting on averages.";

export const VERIFY_UNAVAILABLE_REASON =
  "Team cannot be verified at this time";

export const teamHasRecapSheet = (team) =>
  Boolean(String(team?.recapStoragePath || "").trim());

export const teamBowlersAveragesVerified = (team) =>
  (team?.bowlers || []).every((bowler) => bowler.isVerified || false);

export const teamHasAllRequiredScores = (team) => {
  if (!team?.transformedScores) {
    return false;
  }

  const gamesPerEvent = team.gamesPerEvent || 3;
  const bowlerCount = (team.bowlers || []).length;
  const expectedScoreCount = gamesPerEvent * bowlerCount;
  let actualScoreCount = 0;

  Object.values(team.transformedScores).forEach((bowlerGames) => {
    if (!Array.isArray(bowlerGames)) return;
    bowlerGames.forEach((game) => {
      if (
        game.score !== null &&
        game.score !== "" &&
        game.score !== undefined
      ) {
        actualScoreCount += 1;
      }
    });
  });

  return actualScoreCount >= expectedScoreCount;
};

export const scoreReviewBlockedReason = ({
  team,
  payoutsLocked,
  canVerifyTeamForEdit,
} = {}) => {
  if (payoutsLocked) {
    return "Payouts have been locked and cannot be changed";
  }
  if (!teamHasAllRequiredScores(team)) {
    return "Team must have all scores entered before verification";
  }
  if (!teamBowlersAveragesVerified(team)) {
    return "All bowlers' averages must be verified first";
  }
  if (canVerifyTeamForEdit && !canVerifyTeamForEdit(team?.id)) {
    return VERIFY_UNAVAILABLE_REASON;
  }
  return "";
};

export const pendingScoreReviewTeams = (teams) =>
  (teams || []).filter(
    (team) => teamHasRecapSheet(team) && !team.isVerified
  );

export const recapImportNeedsAverageVerify = (teams, importedTeamIds) => {
  const idSet =
    Array.isArray(importedTeamIds) && importedTeamIds.length
      ? new Set(importedTeamIds)
      : null;
  const selected = (teams || []).filter((team) =>
    idSet
      ? idSet.has(team.id)
      : teamHasRecapSheet(team) && !team.isVerified
  );
  return selected.some((team) => !teamBowlersAveragesVerified(team));
};

export const verifyBlockedOnlyOnAverages = ({
  team,
  payoutsLocked = false,
} = {}) => {
  if (!team || team.isVerified) return false;
  if (payoutsLocked) return false;
  if (!teamHasAllRequiredScores(team)) return false;
  return !teamBowlersAveragesVerified(team);
};

export const showScoresWrittenWaitingCue = ({
  team,
  importedTeamIds,
  payoutsLocked = false,
} = {}) => {
  const ids = new Set(importedTeamIds || []);
  if (!team?.id || !ids.has(team.id)) return false;
  return verifyBlockedOnlyOnAverages({
    team,
    payoutsLocked,
  });
};

const importedTeamLabel = (team) => {
  const who =
    String(team?.teamName || "").trim() ||
    (team?.bowlers || [])
      .map((bowler) => String(bowler?.name || "").trim())
      .filter(Boolean)
      .join(", ") ||
    "This team";
  const lane = laneAssignmentLabel(team) || formatRecapLane(team);
  return lane ? `${who} · ${lane}` : who;
};

export const recapImportAverageWaitNote = (teams, importedTeamIds) => {
  const ids = new Set(importedTeamIds || []);
  if (!ids.size) return "";
  const waiting = (teams || []).filter(
    (team) => ids.has(team?.id) && verifyBlockedOnlyOnAverages({ team })
  );
  if (!waiting.length) return "";
  const labels = waiting.map(importedTeamLabel);
  const shown = labels.slice(0, 3);
  const rest = labels.length - shown.length;
  const list =
    rest > 0 ? `${shown.join("; ")}; and ${rest} more` : shown.join("; ");
  if (labels.length === 1) {
    return `${list}: scores were written. Verify averages, then verify this team.`;
  }
  return `${list}: scores were written. Verify averages, then verify these teams.`;
};
